/**
 * Bộ lọc của trang "Thông báo của tôi" nằm TRÊN URL: tải lại trang, gửi đường
 * dẫn cho đồng nghiệp, bấm "Còn N tin chưa đọc" ở chuông — đều mở đúng tập đó.
 *
 * Giá trị lạ trên URL bị bỏ qua (về mặc định), không làm hỏng trang. Máy chủ
 * vẫn kiểm lại và trả 400 kèm lý do nếu có gì lọt.
 */

import type {
  NotificationGroup,
  NotificationMatch,
  NotificationPageParams,
  NotificationSort,
  NotificationStatus,
} from "../api";

export const GROUPS: readonly NotificationGroup[] = [
  "money", "order", "dispatch", "shift", "photo", "print", "chat", "incident", "pay", "internal",
];

/** Tên nhóm — phải trùng `groupLabels` ở máy chủ (`inbox/kind_catalog.go`). */
export const GROUP_LABEL: Record<NotificationGroup, string> = {
  money: "Tiền",
  order: "Đơn hàng",
  dispatch: "Suất chụp",
  shift: "Ca & nghỉ",
  photo: "Ảnh & hậu kỳ",
  print: "In ảnh",
  chat: "Chat & khách",
  incident: "Sự cố & khiếu nại",
  pay: "Lương & thưởng",
  internal: "Nội bộ",
};

const STATUSES: readonly NotificationStatus[] = ["all", "unread", "read"];
const SORTS: readonly NotificationSort[] = ["newest", "oldest", "unread_first"];
export const PAGE_SIZES = [10, 20, 50, 100] as const;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Ô URL của trang. Ô vắng mặt (hoặc `undefined` — để xoá ô khỏi URL) = mặc định. */
export type NotificationSearch = {
  status?: NotificationStatus | undefined;
  group?: NotificationGroup | undefined;
  kind?: string[] | undefined;
  q?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  sort?: NotificationSort | undefined;
  page?: number | undefined;
  size?: number | undefined;
};

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
}

function oneOf<T extends string>(v: unknown, list: readonly T[]): T | undefined {
  const s = str(v);
  return s && (list as readonly string[]).includes(s) ? (s as T) : undefined;
}

function posInt(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** validateSearch của tuyến: giữ giá trị hợp lệ, bỏ giá trị lạ. */
export function parseNotificationSearch(raw: Record<string, unknown>): NotificationSearch {
  const out: NotificationSearch = {};
  const status = oneOf(raw.status, STATUSES);
  if (status && status !== "all") out.status = status;
  const group = oneOf(raw.group, GROUPS);
  if (group) out.group = group;
  const kinds = (Array.isArray(raw.kind) ? raw.kind : [raw.kind])
    .map(str)
    .filter((k): k is string => !!k && /^[a-z0-9_]{1,60}$/.test(k));
  // Loại chỉ có nghĩa trong một nhóm đang chọn (giao diện chỉ cho tích loại
  // trong nhóm đang mở).
  if (group && kinds.length > 0) out.kind = Array.from(new Set(kinds));
  const q = str(raw.q);
  if (q) out.q = q.slice(0, 100);
  const from = str(raw.from);
  if (from && DAY_RE.test(from)) out.from = from;
  const to = str(raw.to);
  if (to && DAY_RE.test(to)) out.to = to;
  const sort = oneOf(raw.sort, SORTS);
  if (sort && sort !== "newest") out.sort = sort;
  const page = posInt(raw.page);
  if (page && page > 1) out.page = page;
  const size = posInt(raw.size);
  if (size && (PAGE_SIZES as readonly number[]).includes(size) && size !== 20) out.size = size;
  return out;
}

/** Ngày YYYY-MM-DD theo giờ máy (người dùng ở Việt Nam). */
export function dayKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Khoảng ngày mặc định khi URL không có `from`/`to` — chặn trên độ dài hộp
 * thư nếu lượt dọn tin ngừng chạy (kế hoạch 20/10/2026).
 *
 * 91 ngày chứ không 90: lượt dọn mỗi ngày một lần xoá tin quá 90 ngày, nên
 * tin chưa đọc chờ lượt dọn kế tiếp có thể cũ tới ~91 ngày. Lùi ít hơn là
 * chuông còn đỏ mà trang không thấy tin nào.
 */
export function defaultFrom(now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - 91);
  return dayKey(d);
}

/**
 * URL → bộ lọc gửi máy chủ (`match`) — dùng chung cho trang và nút "đã đọc
 * mọi tin đang lọc", để hai chỗ không bao giờ lệch tập.
 */
export function toMatch(s: NotificationSearch, now: Date = new Date()): NotificationMatch {
  const m: NotificationMatch = {};
  if (s.status) m.status = s.status;
  if (s.kind && s.kind.length > 0) m.kind = s.kind;
  else if (s.group) m.group = [s.group];
  if (s.q) m.q = s.q;
  m.from = s.from ?? defaultFrom(now);
  if (s.to) m.to = s.to;
  return m;
}

/** URL → tham số trang gửi máy chủ. */
export function toPageParams(s: NotificationSearch, now: Date = new Date()): NotificationPageParams {
  const size = s.size ?? 20;
  const page = s.page ?? 1;
  const p: NotificationPageParams = { ...toMatch(s, now), limit: size, offset: (page - 1) * size };
  if (s.sort) p.sort = s.sort;
  return p;
}

/** Số tiêu chí lọc đang bật (cho nút "Bộ lọc" trên điện thoại). */
export function activeFilterCount(s: NotificationSearch): number {
  return [s.status, s.group, s.q, s.from || s.to, s.sort].filter(Boolean).length;
}

/** Có tiêu chí nào làm hẹp tập tin không (để biết "trống" là trống thật hay do lọc). */
export function isFiltered(s: NotificationSearch): boolean {
  return !!(s.status || s.group || s.q || s.from || s.to);
}
